import { Box } from '@mui/material';
import { ConfigurationType } from '@server/parser';
import { ClampedText } from '../../../common/ClampedText';
import { InactiveTag } from '../../activity-state/InactiveTag';
import { TypeTag } from '../../shared/TypeTag';
import { CenteredRowWithGap } from '../../universal-search/atoms/CenteredRowWithGap';
import { colors, Tooltip, Typography } from '@sweep-io/sweep-design';
import { DotDivider } from '../../../common/DotDivider';
import { ObjectTypeLabelAndKeyTag } from '../../../common/ObjectTypeLabelAndKey';
import { formatCamelCase } from '../../universal-search/utils';
import { useSelector } from 'react-redux';
import { selectDefaultCreationCrmOrgId } from '../../../../reducers/userInfoReducer';
import { selectSaltoObjectTypes } from '../../../../reducers/global/parserReducer';

interface CardContentProps {
  label: string;
  date?: string;
  apiName: string;
  objectNames?: string[];
  parentObject?: string;
  parentType: ConfigurationType;
  dependencyType: string;
  isActive: boolean;
  withDetailedContentForRelatedObjects?: boolean;
  withApiNameOnHover?: boolean;
}

export const CardContent = ({
  label,
  date,
  apiName,
  parentType,
  dependencyType,
  objectNames,
  parentObject,
  isActive,
  withDetailedContentForRelatedObjects,
  withApiNameOnHover,
}: CardContentProps) => {
  const crmOrgId = useSelector(selectDefaultCreationCrmOrgId);
  const saltoObjectTypes = useSelector(selectSaltoObjectTypes(crmOrgId ?? ''));
  const objectsLabels = objectNames
    ?.slice(1)
    .map(
      (objectName) =>
        `${saltoObjectTypes?.find((type) => type.objectType === objectName)?.label ?? formatCamelCase(objectName)} (${objectName})`,
    )
    .join(', ');

  const renderObjects = () => {
    if (!objectNames?.length) return <></>;
    return (
      <>
        <ObjectTypeLabelAndKeyTag
          key={objectNames?.[0]}
          objectType={objectNames[0]}
          tagVariant="tiny"
        />

        {objectNames.length > 1 && (
          <Tooltip title={objectsLabels}>
            <Typography variant="caption" color={colors.grey[700]}>
              +{objectNames.length - 1}
            </Typography>
          </Tooltip>
        )}
      </>
    );
  };

  const isReferencingTo = !objectNames?.includes(parentObject ?? '');

  return (
    <Box display="flex" pr={2} pl={2} flexWrap="wrap" alignItems="center" minWidth={0}>
      <CenteredRowWithGap>
        <ClampedText
          text={label}
          tooltip={withApiNameOnHover ? apiName : ''}
          textColor={isActive ? 'inherit' : colors.grey[500]}
          disableHoverListener={!withApiNameOnHover}
        />
      </CenteredRowWithGap>

      {!withDetailedContentForRelatedObjects && <InactiveTag isActive={isActive} />}

      <Box width="100%" display="flex" gap={0.5} pt={0.5}>
        {withDetailedContentForRelatedObjects && (
          <>
            <CenteredRowWithGap gap={0.5}>
              <TypeTag configurationType={parentType} />
              {parentType !== ConfigurationType.fields &&
                dependencyType &&
                dependencyType !== parentType && (
                  <>
                    <DotDivider />
                    <Typography
                      variant="caption"
                      color={isActive ? colors.grey[700] : colors.grey[500]}
                      whiteSpace="nowrap"
                    >
                      {dependencyType}
                    </Typography>
                  </>
                )}

              {parentType === ConfigurationType.fields &&
                dependencyType &&
                dependencyType !== parentType && (
                  <>
                    <DotDivider />
                    <Typography
                      variant="caption"
                      color={isActive ? colors.grey[700] : colors.grey[500]}
                      whiteSpace="nowrap"
                    >
                      {dependencyType} {isReferencingTo ? ' to ' : ' from '}
                    </Typography>
                    {renderObjects()}
                  </>
                )}
            </CenteredRowWithGap>

            {!!objectNames?.length && parentType !== ConfigurationType.fields && (
              <>
                <DotDivider />
                {renderObjects()}
              </>
            )}

            {!isActive && (
              <>
                <DotDivider /> <InactiveTag isActive={isActive} />
              </>
            )}
          </>
        )}
        {date && !withDetailedContentForRelatedObjects && isActive && (
          <Typography variant="caption" color={colors.grey[700]}>
            Last updated {date}
          </Typography>
        )}
      </Box>
    </Box>
  );
};
